import { GLOBAL_FILTER_SECTION_ALLOWED_DATA_TYPES } from '../../company-config.constant';
import { ICustomFieldDefinition } from '../../CustomFields/interfaces/custom-fields.interface';
import {
  GlobalFilterEntityOption,
  GlobalFilterSectionOption,
  GlobalSearchDropdownOption,
} from '../globalSearchBar.interface';

export const createEmptyFilterSectionRowId = (): string =>
  `filter-row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const toFilterSectionOptionValue = (
  entityTypeId: number,
  keyName: string
): string => `${entityTypeId}:${keyName}`;

export const isFilterSectionOptionValue = (value: string): boolean =>
  typeof value === 'string' && /^\d+:.+$/.test(value);

export const parseFilterSectionOptionValue = (
  value: string
): { entityTypeId: number; keyName: string } | null => {
  if (!isFilterSectionOptionValue(value)) {
    return null;
  }

  const separatorIndex = value.indexOf(':');
  const entityTypeId = Number(value.slice(0, separatorIndex));
  const keyName = value.slice(separatorIndex + 1);

  if (!Number.isFinite(entityTypeId) || !keyName) {
    return null;
  }

  return { entityTypeId, keyName };
};

export const getEntityViewPath = (
  entityTypeId: number,
  id: number | string
): string => `/entity/${entityTypeId}/view/${id}`;

export const FILTER_SECTION_HEADER_OPTION_META = {
  icon: 'pi pi-table',
  className: 'text-blue-600',
};

export const mapReportRowToSearchItem = (
  row: Record<string, unknown>,
  entityTypeId: number,
  keyName: string
) => ({
  id: row.id as number | string,
  entityTypeId,
  keyName,
  displayValue: row[keyName] == null ? '' : String(row[keyName]),
});

export const uniqueEntityTypeIds = (ids: number[]): number[] =>
  ids.filter((id, index) => ids.indexOf(id) === index);

export const toEntityDropdownOptions = (
  entities: unknown[] | undefined
): GlobalFilterEntityOption[] =>
  (entities ?? []).reduce<GlobalFilterEntityOption[]>((options, entity) => {
    if (!entity || typeof entity !== 'object') {
      return options;
    }

    const item = entity as {
      entity_type_id?: number | string;
      name?: string;
      entity_form_name?: string;
      report_display_name?: string;
    };
    const value = Number(item.entity_type_id);
    const label =
      item.name || item.entity_form_name || item.report_display_name || '';

    if (!Number.isFinite(value) || !label) {
      return options;
    }

    options.push({ label, value });
    return options;
  }, []);

export const mapAllowedFieldOptions = (
  fields: ICustomFieldDefinition[]
): GlobalSearchDropdownOption[] =>
  fields
    .filter(
      field =>
        Boolean(field.key_name) &&
        Boolean(field.label) &&
        GLOBAL_FILTER_SECTION_ALLOWED_DATA_TYPES.includes(
          field.data_type as string
        )
    )
    .map(field => ({
      label: field.label,
      value: field.key_name as string,
    }));

export const FILTER_SECTION_CATALOG_UPDATED_EVENT =
  'filter-section-catalog-updated';

let memoryCatalog: GlobalSearchDropdownOption[] = [];

const isLabeledSearchOption = (option: GlobalSearchDropdownOption) =>
  Boolean(option?.label?.trim()) && Boolean(option?.value);

const getCatalogStorageKey = (): string => {
  const clientId =
    (typeof window !== 'undefined' && localStorage.getItem('client_id')) ||
    'default';
  return `global-filter-section-search-options:${clientId}`;
};

export const getCachedFilterSectionSearchOptions =
  (): GlobalSearchDropdownOption[] => {
    if (memoryCatalog.length) {
      return memoryCatalog;
    }

    try {
      const raw = localStorage.getItem(getCatalogStorageKey());
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) {
        memoryCatalog = parsed.filter(isLabeledSearchOption);
      }
    } catch {
      memoryCatalog = [];
    }

    return memoryCatalog;
  };

export const setCachedFilterSectionSearchOptions = (
  options: GlobalSearchDropdownOption[]
) => {
  memoryCatalog = (options || []).filter(isLabeledSearchOption);

  try {
    localStorage.setItem(getCatalogStorageKey(), JSON.stringify(memoryCatalog));
  } catch {
    // Ignore storage quota / private-mode failures.
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(FILTER_SECTION_CATALOG_UPDATED_EVENT));
  }
};

export const resolveFilterSectionSearchOptions = (
  saved: GlobalFilterSectionOption[]
): GlobalSearchDropdownOption[] => {
  if (!saved.length) {
    return [];
  }

  return saved.reduce<GlobalSearchDropdownOption[]>((options, item) => {
    const label = item.label?.trim();
    if (!item.entity_type_id || !item.key_name || !label) {
      return options;
    }

    options.push({
      label,
      value: toFilterSectionOptionValue(item.entity_type_id, item.key_name),
    });
    return options;
  }, []);
};

export const pruneRemovedFilterSectionValues = (
  searchOptions: string[],
  nextResolvedValues: string[]
): { searchOptions: string[]; changed: boolean } => {
  const nextSet = new Set(nextResolvedValues);
  const nextSearchOptions = searchOptions.filter(
    value => !isFilterSectionOptionValue(value) || nextSet.has(value)
  );
  const changed =
    nextSearchOptions.length !== searchOptions.length ||
    nextSearchOptions.some((value, index) => value !== searchOptions[index]);

  return { searchOptions: nextSearchOptions, changed };
};
